import {
  GreyGrey60,
  GreyWhite,
  PANEL_GUTTER,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useNavigationV3Context } from 'components/NavigationV3/hooks/useNavigationV3Context'
import { useNavigationWidth } from 'components/NavigationV3/hooks/useNavigationWidth'
import {
  NAVIGATION_ANIMATION,
  getNavigationAnimationStyles
} from 'components/NavigationV3/constants/animation'
import { getWidthAnimationStyles } from 'components/NavigationV3/constants/animationUtils'
import { useIsMobile } from 'hooks/useIsMobile'

// Navigation widths
export const EXPANDED_WIDTH = 220
export const EXPANDED_WIDTH_REV_REC = 220
export const COLLAPSED_WIDTH = 48

export const Container = ({ children }: { children: React.ReactNode }) => {
  const { data, functions } = useNavigationV3Context()
  const { isNavigationCollapsed, isEffectivelyCollapsed, isMobileNavOpen } =
    data
  const { toggleNavigationCollapsed, closeMobileNav } = functions
  const isMobile = useIsMobile()
  const desktopWidth = useNavigationWidth()

  const widthAnimationStyles = getWidthAnimationStyles()

  return (
    <>
      {isMobile && isMobileNavOpen && (
        <Box
          data-testid="layout-sidebar-backdrop"
          position="fixed"
          inset="0"
          zIndex={39}
          backgroundColor="rgba(0, 0, 0, 0.4)"
          onClick={closeMobileNav}
        />
      )}
      <Box
        // Mobile: a fixed slide-over drawer toggled by the page-header burger.
        // Desktop: the redesigned sticky in-flow sidebar column.
        position={{ base: 'fixed', md: 'sticky' }}
        top="0"
        left="0"
        height={{ base: '100dvh', md: '100vh' }}
        width={{ base: `${EXPANDED_WIDTH}px`, md: `${desktopWidth}px` }}
        minWidth={{ base: `${EXPANDED_WIDTH}px`, md: `${desktopWidth}px` }}
        maxWidth={{ base: `${EXPANDED_WIDTH}px`, md: `${desktopWidth}px` }}
        flexShrink={0}
        zIndex={{ base: 40, md: 2 }}
        boxShadow={{ base: isMobileNavOpen ? 'lg' : 'none', md: 'none' }}
        // transform/transition live in `sx` (not as props) so they override the
        // `translateZ(0)` and width-transition that getWidthAnimationStyles also
        // sets via sx — otherwise the mobile slide would never animate.
        sx={{
          ...widthAnimationStyles,
          transform: {
            base: isMobileNavOpen ? 'translateX(0)' : 'translateX(-100%)',
            md: 'translateZ(0)'
          },
          // Hidden, not just off-screen: translated links stay tabbable. The
          // delay below keeps it visible through the slide-out.
          visibility: {
            base: isMobileNavOpen ? 'visible' : 'hidden',
            md: 'visible'
          },
          // Mobile slide plus desktop collapse: each only fires on its own
          // breakpoint.
          transition: [
            `transform ${NAVIGATION_ANIMATION.DRAWER_DURATION} ${NAVIGATION_ANIMATION.DRAWER_EASING}`,
            `visibility 0s linear ${isMobileNavOpen ? '0s' : NAVIGATION_ANIMATION.DRAWER_DURATION}`,
            widthAnimationStyles.transition
          ].join(', '),
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none'
          }
        }}
      >
        {/**
         * On desktop the nav sits directly on the gray backdrop (transparent
         * surface) so the redesigned shell reads as nav + floating content
         * panels. The top padding aligns the nav header with the top of those
         * panels: the gutter (PANEL_GUTTER) plus 1px to match the panel's top
         * border, which offsets the panel's header content by 1px. On mobile the
         * surface is opaque so the slide-over drawer doesn't show the page
         * content behind it.
         */}
        <Flex
          data-testid="layout-sidebar-surface"
          direction="column"
          justifyContent="flex-start"
          width="100%"
          height="100%"
          paddingTop={`calc(${PANEL_GUTTER} + 1px)`}
          paddingBottom={`calc(${PANEL_GUTTER} + 1px)`}
          bg={{ base: GreyWhite, md: 'transparent' }}
          overflow="hidden"
          sx={getNavigationAnimationStyles(isEffectivelyCollapsed)}
        >
          {children}
        </Flex>
        {/**
         * Desktop-only collapse/expand handle — the mobile drawer is toggled by
         * the page-header burger instead. Instead of a floating button, the
         * nav's right edge acts as a resize affordance: hovering it highlights a
         * hairline and shows the resize cursor; clicking toggles the collapsed
         * state. (The `[` / `]` keyboard shortcuts do the same — see
         * useKeyboardNavigation.)
         */}
        <Box
          data-testid="layout-sidebar-toggle"
          display={{ base: 'none', md: 'block' }}
          role="button"
          aria-label={
            isNavigationCollapsed ? 'Expand navigation' : 'Collapse navigation'
          }
          onClick={toggleNavigationCollapsed}
          position="absolute"
          top="0"
          // Straddle the nav/panel boundary (negative right) so the handle is
          // grabbable from both sides of the edge, not only the nav side. Kept
          // narrow (4px each side of the seam) so it sits in the nav's right
          // padding gap and doesn't steal clicks from nav items or page content.
          right="-4px"
          width="8px"
          height="100%"
          // Directional cursor hints which way the click will move the edge:
          // expanded collapses the nav leftward (w-resize), collapsed expands it
          // rightward (e-resize). It isn't a free-form drag, so a plain ew-resize
          // over-promised a two-way resize.
          cursor={isNavigationCollapsed ? 'e-resize' : 'w-resize'}
          zIndex={3}
          sx={{ '&:hover > div': { opacity: 1 } }}
        >
          {/**
           * The hairline sits on the nav/panel boundary. It is inset 16px from
           * the top/bottom of the content panel; the panel sits `PANEL_GUTTER`
           * (8px) below the viewport top, so the inset from the viewport is
           * 8px + 16px = 24px on each end.
           */}
          <Box
            position="absolute"
            top="24px"
            bottom="24px"
            right="3px"
            width="2px"
            backgroundColor={GreyGrey60}
            opacity={0}
            transition="opacity 0.15s ease"
          />
        </Box>
      </Box>
    </>
  )
}
